import { parse as parseQueryStringParams, stringify as toQueryString } from '@AppModels/QueryStringUtils';
import * as React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { extractFolderNamesFromKeywords, filterTemplatesByFolder, FolderInfo } from '../folderUtils';

interface UseTemplateFoldersProps<T extends { keywords?: string[] }> {
	templates: T[];
	allKeywords?: string[];
}

interface UseTemplateFoldersResult<T> {
	folders: FolderInfo[];
	selectedFolder: string | null;
	setSelectedFolder: (folder: string | null) => void;
	filteredTemplates: T[];
}

/**
 * Hook for managing template folder filtering.
 * Handles folder extraction from keywords, URL state management, and template filtering.
 */
export const useTemplateFolders = <T extends { keywords?: string[] }>({
	templates,
	allKeywords = [],
}: UseTemplateFoldersProps<T>): UseTemplateFoldersResult<T> => {
	const history = useHistory();
	const location = useLocation();

	// Parse folder from URL query params - useLocation provides reactive updates
	const selectedFolder = React.useMemo(() => {
		const params = parseQueryStringParams(location.search) as { folder?: string };
		return params.folder || null;
	}, [location.search]);

	// Extract folders from all available keywords (from API)
	const folders = React.useMemo(() => {
		return extractFolderNamesFromKeywords(allKeywords);
	}, [allKeywords]);

	// Update URL when folder changes
	const setSelectedFolder = React.useCallback(
		(folder: string | null) => {
			const currentParams = parseQueryStringParams(location.search) as Record<string, string | undefined>;

			if (folder) {
				currentParams.folder = folder;
			} else {
				delete currentParams.folder;
			}

			const newSearch = toQueryString(currentParams);
			history.replace({
				...location,
				search: newSearch ? `?${newSearch}` : '',
			});
		},
		[history, location]
	);

	// Filter templates based on selected folder
	const filteredTemplates = React.useMemo(() => {
		return filterTemplatesByFolder(templates, selectedFolder);
	}, [templates, selectedFolder]);

	return {
		folders,
		selectedFolder,
		setSelectedFolder,
		filteredTemplates,
	};
};
